<template>
  <div class="page page-highlighter bg-gray-darken">
    <a class="download-thumbnail" ref="downloadThumbnail"></a>
    <div class="loading-container" v-if="isLoading">
      <div class="loading">
        <strong><i class="fas fa-history mr-1 rotate-center"></i></strong> ByteArk TimeMachine
      </div>
    </div>
    <div v-if="!isLoading && hasChannel">
      <ChooseChannelModal
        v-if="state.isChooseChannelModalShowing && channels"
        :channels="channels"
        :currentChannel="selectedSource"
        :onClose="onCloseChooseChannelModal"
        :onSubmit="onChooseChannel"/>
      <div class="section section-preview">
        <div class="container">
          <div class="row">
            <div class="col-md-9 capture-stream__clip-preview">
              <PreviewByteArkPlayer
                ref="videoPlayer"
                v-model="thumbnailData"
                :options="videoOptions"
                :statusMessage="statusPreview"
                :toggleSourcesModal="toggleSourcesModal" />
            </div>
            <div class="col-md-3">
              <div class="section-sources">
                <div class="source-info">
                  <div class="source-content current-source">
                    <div class="content-container">
                      <h5 class="channel-text" v-if="state.isLoading">Loading...</h5>
                      <h5 class="channel-text" v-else-if="selectedSource"><i class="fas fa-video"></i> {{selectedSource.name}}</h5>
                      <h5 class="channel-text" v-else-if="!state.isLoading &&!selectedSource">{{$t('capture.source.empty')}}</h5>
                    </div>
                    <div class="button-container">
                      <button
                        type="button"
                        class="btn source-button change-source-button"
                        @click.stop="toggleSourcesModal()">
                        {{$t('capture.source.change')}}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <HistoryLog
                :channels="channels"
                ref="historyLog" />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!isSafari"
        class="section section-control">
        <div class="container">
          <ClipEditor
            v-if="hasVideoObject"
            :video="video"
            :duration="duration"
            :timeUpdate="timeupdate"
            :onSubmit="downloadSource"
            :onExportThumbnail="onExportThumbnail"
            :statusMessage="statusDownload"
            :hasVideoInPeriod="hasVideoInPeriod" />
          <div
            v-if="!hasVideoObject"
            class="error-message-container">
            <p class="error-message">
              {{$t('response:video.dateTimeUnavailable.title')}}
              {{$t('response:video.generalError.helpText')}}
            </p>
            <button
              v-if="minDateTime"
              type="button"
              class="btn source-button change-time-button"
              @click.stop="showDateTimeModal">
              {{$t('capture.timeline.selectDateTime')}}
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="isSafari"
        class="no-safari-notice">
        <i class="fas fa-exclamation-triangle"></i> {{$t('noSafariNotice')}}
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';
import {
  addHours,
  addMilliseconds,
  format,
  parseISO,
} from 'date-fns';
import {
  downloadClip,
  getLive,
  getPreviews,
  // loadChannels,
  loadChannelById,
} from '@/api/TimeMachine';
import ChooseChannelModal from './parts/channel/ChooseChannelModal.vue';
import PreviewByteArkPlayer from './elements/PreviewByteArkPlayer.vue';
import HistoryLog from './elements/HistoryLog.vue';
import ClipEditor from './ClipEditor.vue';

export default {
  components: {
    ChooseChannelModal,
    PreviewByteArkPlayer,
    HistoryLog,
    ClipEditor,
  },
  data() {
    return {
      video: {
        src: '',
        start: '',
        end: '',
      },
      state: {
        isLoading: true,
        isChooseChannelModalShowing: false,
        liveError: null,
      },
      statusDownload: {
        isSaving: false,
        isSaved: false,
        error: null,
      },
      statusPreview: {
        error: null,
        type: null,
      },
      selectedSource: null,
      preview: {},
      videoOptions: {
        autoplay: true,
        muted: false,
        debug: true,
        liveui: true,
      },
      playerEvent: null,
      isRandom: 0,
      timeupdate: null,
      duration: 0,
      thumbnailData: '',
      fragments: {},
      isSafari: false,
    };
  },
  created() {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') !== -1) {
      this.isSafari = ua.indexOf('chrome') <= -1;
    }
    this.load();
  },
  computed: {
    ...mapState({
      channels: state => state.teamInformation.channels,
      isLoading: state => state.teamInformation.loadingState.isLoading,
      minDateTime: state => state.timeline.minDateTime,
    }),
    ...mapGetters(['selectedDateTime']),
    teamName() {
      return this.$route.params.teamName;
    },
    hasChannel() {
      return this.channels && this.channels.length !== 0;
    },
    hasVideoObject() {
      if (!this.video) {
        return false;
      }
      const { start, end, src } = this.video;
      return start && end && src;
    },
    hasVideoInPeriod() {
      return this.hasVideoObject && _.isEmpty(this.statusPreview.error);
    },
  },
  watch: {
    async selectedDateTime(value) {
      if (value && this.selectedSource) {
        // NOTE: Out of live mode
        await this.onChangeData();
      } else if (value === '' && this.selectedSource) {
        // NOTE: Back to live mode
        const response = await this.loadLiveStream(this.selectedSource._id);
        this.preview = response.data;
        await this.setPreviewSourceData();
        await this.triggerChangeSourcePlayer(this.preview.url);
      }
    },
    selectedSource(value) {
      if (value && this.selectedDateTime) {
        this.onChangeData();
      }
    },
  },
  methods: {
    async load() {
      const { query } = this.$route;
      await this.$store.dispatch('loadChannels');

      if (!this.hasChannel) {
        await this.$router.push(`/${this.teamName}/channel/create`);
      } else {
        await this.setPreviewDefaultSource();
      }

      // Load channel from query string
      if (query && query.channel) {
        const id = query.channel;
        const selectedChannel = this.channels.find(channel => channel._id === id);
        const defaultChannel = _.head(this.channels);

        if (defaultChannel._id !== selectedChannel._id) {
          await this.onSelectSource(selectedChannel);
        }
      }
    },
    async setPreviewDefaultSource() {
      await this.onSelectSource(_.head(this.channels));
    },
    async onChangeData() {
      const selectedTime = this.selectedDateTime;
      let start;
      let end;

      if (!selectedTime) {
        start = new Date();
        end = addHours(new Date(), 10);
      } else {
        start = new Date(this.selectedDateTime);
        end = addHours(new Date(this.selectedDateTime), 10);
      }

      const payload = {
        channelId: this.selectedSource._id,
        start,
        end,
      };
      await this.onChangeSamplePreview(payload);
    },
    async onChangeSamplePreview(payload) {
      this.statusPreview.error = null;
      try {
        const response = await getPreviews(payload.channelId, payload.start, payload.end);
        this.preview = response.data;
        await this.setPreviewSourceData();
        await this.triggerChangeSourcePlayer(this.preview.url);
      } catch (error) {
        this.statusPreview.error = error.response;
        this.statusPreview.type = 'previousProgram';

        if (this.$refs.videoPlayer.player && !this.$refs.videoPlayer.player.paused()) {
          this.$refs.videoPlayer.player.pause();
        }
      }
    },
    async downloadSource(chunkPayload) {
      this.statusDownload.isSaving = true;
      try {
        const payload = {
          title: chunkPayload.title,
          channelId: this.selectedSource._id,
          startedAt: chunkPayload.start,
          endedAt: chunkPayload.end,
        };
        await downloadClip(payload);
        this.$refs.historyLog.load();
        this.statusDownload.isSaving = false;
      } catch (error) {
        this.statusDownload.error = error.response;
        this.statusDownload.isSaving = false;
      }
    },
    setPreviewSourceData() {
      const start = this.preview.startedAt;
      const end = this.preview.endedAt;

      this.video = {
        src: this.selectedSource,
        start,
        end,
      };
    },
    onCloseChooseChannelModal() {
      this.state.isChooseChannelModalShowing = false;
    },
    async appendChannelId(id) {
      const { query } = this.$route;
      if (query && query.channel && query.channel === id) {
        return;
      }
      await this.$router.push({ query: { channel: id } });
    },
    onChooseChannel(selectedSource) {
      this.onSelectSource(selectedSource);
      this.onCloseChooseChannelModal();
    },
    async onSelectSource(selectedSource) {
      this.state.isLoading = true;
      this.statusPreview.error = null;
      await this.$store.dispatch('setMinDateTime', null);
      await this.$store.dispatch('setMaxDateTime', null);

      await this.appendChannelId(selectedSource._id);

      const response = await loadChannelById(selectedSource._id);
      const channelInfo = response.data;
      const streaming = _.get(channelInfo, 'streaming.availabled', null);
      this.state.isLoading = false;
      this.selectedSource = { ...this.selectedSource, ...channelInfo };
      if (streaming) {
        const streamStarted = _.get(channelInfo, 'streaming.contentStartedAt', null);
        const streamEnded = _.get(channelInfo, 'streaming.contentEndedAt', null);
        const minDateTime = addHours(parseISO(streamStarted), 1);
        const maxDateTime = parseISO(streamEnded);
        await this.$store.dispatch('setMinDateTime', minDateTime);
        await this.$store.dispatch('setMaxDateTime', maxDateTime);
        // TODO: Move this to function, we're using too much of this on this file
        try {
          const responsePreview = await this.loadLiveStream(this.selectedSource._id);
          this.preview = responsePreview.data;
          await this.setPreviewSourceData();
          await this.triggerChangeSourcePlayer(this.preview.url);
          this.state.isLoading = false;
        } catch (error) {
          if (streaming.contentStartedAt) {
            this.$store.commit('setDateTimeModal', true);
          } else {
            this.statusPreview.error = error.response;
          }
          this.state.isLoading = false;
        }
      } else {
        this.video = null;
        this.preview = null;
        this.statusPreview.type = 'live';
        this.statusPreview.error = this.$t('validation:cannotFindSource');
      }
      this.state.isLoading = false;
    },
    triggerChangeSourcePlayer(newSource) {
      this.$store.commit('loadStreamByUrl', { url: newSource });
    },
    onChangePlayerSize(size) {
      this.state.playerSize = size;
    },
    async onExportThumbnail() {
      if (!this.$refs.videoPlayer) {
        return;
      }

      this.thumbnailData = await this.$refs.videoPlayer.getThumbnail();
      const img = this.$refs.downloadThumbnail;
      img.href = this.thumbnailData;
      img.download = format(addMilliseconds(new Date(this.video.start), this.timeupdate * 1000), 'MMMM dd yyyy, HH-mm-ss');
      img.click();
    },
    toggleSourcesModal() {
      this.state.isChooseChannelModalShowing = !this.state.isChooseChannelModalShowing;
    },
    async loadLiveStream(sourceId) {
      try {
        return await getLive(sourceId);
      } catch (error) {
        this.statusPreview.error = this.$t('validation:cannotFindSource');
        this.statusPreview.type = 'live';
        this.state.isLoading = false;
        return null;
      }
    },
    showDateTimeModal() {
      this.$store.dispatch('showDateTimeModal');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/themes/helper.scss';
@import '~@/assets/scss/variables.scss';

.no-safari-notice {
  padding: 30px;
  background-color: #a70000;
  color: #FFF;
  text-align: center;
}

.section-sources {
  width: 100%;
  padding-top: $spacer;
  .source-info {
    width: 100%;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    align-items: flex-start;
    .source-content {
      flex: 0 0 100%;
      margin-bottom: 20px;
      .content-container {
        .channel-text {
          font-weight: 700;
          margin-bottom: 4px;
        }
        .is-live {
          font-size: $font-size-base;
          margin-top: -22px;
        }
        ::v-deep .vdatetime {
          margin-top: -4px;
        }
        ::v-deep .form-control-custom {
          font-size: $font-size-base * 0.9;
          margin-bottom: 4px;
          background: #222;
          border: none;
          color: $body-color;
        }
      }
      .button-container {
        .source-button {
          background: transparent;
          color: $body-color;
          border: 1px solid #424343;
          padding: 3px 10px;
          font-size: $font-size-base * 0.7;
          margin-right: 6px;
          transition: all 250ms ease;
          &:hover {
            background: $body-color;
            color: $body-bg;
            border: 1px solid transparent;
          }
        }
      }
    }
  }
}

.preview-sizes-container {
  text-align: right;
  .btn-link {
    color: $gray-600;
  }
  .expand-icon {
    transform: scaleX(-1);
  }
}

.section-preview {
  background-color: $dark-primary;
  padding-top: $spacer * 0.5;
}

.section-control {
  background-color: $dark-secondary;
}

.error-message-container {
  padding: 20px;
  text-align: center;
  margin: 0;

  .error-message {
    margin-bottom: 6px;
  }

  .source-button {
    font-size: $font-size-base * 0.8;
  }
}

@media screen and (min-width: 768px) {
  .section-preview {
    .col-md-9 {
      flex: 0 0 69%;
      max-width: 69%;
    }
    .col-md-3 {
      flex: 0 0 30%;
      max-width: 30%;
    }
  }
}

@media screen and (max-width: 1440px) {
  .section-preview {
    .col-md-9 {
      flex: 0 0 60%;
      max-width: 60%;
    }
    .col-md-3 {
      flex: 0 0 40%;
      max-width: 40%;
    }
  }
  .section-sources {
    padding-top: 2px;
  }
}

@media screen and (max-width: 1366px) {
  .section-preview {
    .col-md-9 {
      flex: 0 0 52%;
      max-width: 52%;
    }
    .col-md-3 {
      margin: 0 auto;
    }
  }
  .section-sources {
    .source-info {
      .source-content {
        display: flex;
        flex-flow: row;
        margin-bottom: 10px;
        .channel-text {
          margin: 0 $spacer 0 0;
        }
      }
    }
  }
}

@media screen and (max-width: 1060px) {
  .section-preview {
    .col-md-9 {
      flex: 0 0 70%;
      max-width: 70%;
    }
    .col-md-3 {
      flex: 0 0 30%;
      max-width: 30%;
    }
  }
  .section-sources {
    .source-info {
      .source-content {
        display: block;
        .channel-text {
          margin: 0 0 6px 0;
        }
      }
    }
  }
}


@media screen and (max-width: 768px) {
  .section-preview {
    .col-md-9 {
      flex: 0 0 100%;
      max-width: 100%;
    }
    .col-md-3 {
      margin: $spacer * 2 0;
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
  .section-sources {
    padding-top: 2px;
  }
}
</style>
